import { useState } from "react";
import { Loader2, CreditCard, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const inputClass =
  "h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

const today = () => new Date().toISOString().slice(0, 10);

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/60">{label}</span>
      {children}
    </label>
  );
}

function RegisterForm({ course, courseType, testPrefix, onDone }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "", start_date: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your full name");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Please enter a valid email");
    if (!/^\+?[0-9 ()-]{7,20}$/.test(form.phone.trim())) return toast.error("Please enter a valid phone number");
    if (!form.start_date) return toast.error("Please choose a preferred start date");
    setSubmitting(true);
    const framed = window.self !== window.top;
    const tab = framed ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post("/registrations/checkout", {
        ...form, course_id: course.id, course_type: courseType, origin_url: window.location.origin,
      });
      if (!data?.checkout_url) throw new Error("Could not start payment. Please try again.");
      if (tab) {
        tab.location.href = data.checkout_url;
        toast.success("Stripe checkout opened in a new tab");
        onDone();
      } else if (framed) {
        setSubmitting(false);
        toast("Pop-up blocked — open secure checkout", {
          duration: 20000,
          action: { label: "Open checkout", onClick: () => window.open(data.checkout_url, "_blank") },
        });
      } else {
        window.location.assign(data.checkout_url);
      }
    } catch (err) {
      if (tab) tab.close();
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-4" data-testid={`${testPrefix}-form`}>
      <Field label="Full name">
        <input data-testid={`${testPrefix}-name-input`} className={inputClass} value={form.name}
          onChange={set("name")} placeholder="Your full name" autoComplete="name" />
      </Field>
      <Field label="Email">
        <input data-testid={`${testPrefix}-email-input`} type="email" className={inputClass} value={form.email}
          onChange={set("email")} placeholder="you@email.com" autoComplete="email" />
      </Field>
      <Field label="Phone number">
        <input data-testid={`${testPrefix}-phone-input`} type="tel" className={inputClass} value={form.phone}
          onChange={set("phone")} placeholder="+92 300 1234567" autoComplete="tel" />
      </Field>
      <Field label="Course selected">
        <input data-testid={`${testPrefix}-course-input`} className={`${inputClass} bg-secondary/60 font-semibold text-forest-deep`}
          value={course.title} readOnly />
      </Field>
      <Field label="Preferred start date">
        <input data-testid={`${testPrefix}-start-date-input`} type="date" min={today()} className={inputClass}
          value={form.start_date} onChange={set("start_date")} />
      </Field>
      <button type="submit" disabled={submitting} data-testid={`${testPrefix}-submit-btn`}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(212,175,55,0.4)] disabled:opacity-60">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
        Proceed to payment
      </button>
      <p className="text-center text-xs text-muted-foreground">Secure payment powered by Stripe</p>
    </form>
  );
}

export function RegisterCourseButton({ course, courseType, testId, className, label = "Register" }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <button type="button" data-testid={testId}
        onClick={(e) => { e.preventDefault(); setOpen(true); }}
        className={className || "inline-flex items-center gap-1.5 rounded-full bg-forest px-4 py-2 text-xs font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5"}>
        <GraduationCap className="h-3.5 w-3.5" /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-[2rem] border-border bg-white p-8 sm:max-w-md" data-testid={`${testId}-modal`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">Course registration</p>
          <DialogTitle className="font-display text-2xl font-extrabold text-forest-deep">{course.title}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Fill in your details, then complete payment to confirm your seat.
          </DialogDescription>
          {open && <RegisterForm course={course} courseType={courseType} testPrefix={`${testId}-modal`} onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </span>
  );
}
